'use client';

import { Suspense, useEffect, useState } from 'react';
import { useSearchParams } from 'next/navigation';
import DetailsNeededPrompt from './DetailsNeededPrompt';
import { useForYou } from './useForYou';

/** Without ?complete=details the page shows briefly before the prompt covers it. */
export const AUTO_OPEN_DELAY_MS = 1200;

/**
 * On an event page: opens the details prompt once per visit when the viewer's
 * enrollment here is missing details (spec §5.3). React state only, so every
 * new visit asks again; closable. `?complete=details` (the email target) opens
 * it as soon as the item is known. Signed out: useForYou fetches nothing.
 */
function AutoPrompt({ contentId }: { contentId: string }) {
  const searchParams = useSearchParams();
  const immediate = searchParams?.get('complete') === 'details';
  const { data } = useForYou();
  const enrollmentId = data?.find(i => i.type === 'details_needed' && i.contentId === contentId)?.enrollmentId ?? null;
  const [shownFor, setShownFor] = useState<string | null>(null);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (!enrollmentId || shownFor === enrollmentId) return;
    const show = () => { setShownFor(enrollmentId); setOpen(true); };
    if (immediate) {
      show();
      return;
    }
    const timer = setTimeout(show, AUTO_OPEN_DELAY_MS);
    return () => clearTimeout(timer);
  }, [enrollmentId, shownFor, immediate]);

  if (!enrollmentId && !shownFor) return null;
  return (
    <DetailsNeededPrompt
      enrollmentId={shownFor ?? enrollmentId!}
      open={open}
      onClose={() => setOpen(false)}
    />
  );
}

/** useSearchParams needs a Suspense boundary; the prompt is never worth holding the page for. */
export default function EventDetailsAutoPrompt({ contentId }: { contentId: string }) {
  return (
    <Suspense fallback={null}>
      <AutoPrompt contentId={contentId} />
    </Suspense>
  );
}
